import React from "react";
import DropDeskIcon from "@dropDesk/storage/icons";
import { ICONS_DROPDESK } from "@dropDesk/storage/icons/icons.d";
import {
    Badge,
    BadgeRow,
    BenefitDescription,
    BenefitIcon,
    BenefitItem,
    BenefitList,
    BenefitText,
    BenefitTitle,
    Body,
    ButtonPrimary,
    ButtonSecondary,
    Callout,
    CalloutText,
    CheckItem,
    Checklist,
    CheckText,
    CloseButton,
    Container,
    FeatureCardCenter,
    FeatureCardFixed,
    FeatureContent,
    FeatureImage,
    FeatureSubtitle,
    FeatureTitle,
    Footer,
    InvestmentBold,
    InvestmentCalloutPinned,
    InvestmentText,
    LeftColumn,
    LeftDescription,
    LeftTitle,
    MegaphoneWrapper,
    PriceBox,
    PriceLabel,
    PriceNote,
    PricePeriod,
    PriceValue,
    RightColumn,
    RightIntro,
    RightIntroSubtitle,
    RightIntroText,
    RightIntroTitle,
    TitleAccent,
} from "@dropDesk/presentation/pages/dashboard/ui/modals/styled";
import { OrangeAppColors, PurpleAppColors, SharedColors } from "@dropDesk/domain/entities/theme/app_colors";
import { ColorScheme } from "@dropDesk/domain/entities/theme/color_scheme";
import chatInternoBanner from "@dropDesk/storage/images/chat_interno_banner.png";

const BENEFITS = [
    {
        icon: ICONS_DROPDESK.internalChat,
        title: "Converse com sua equipe",
        description: "Troque mensagens privadas ou em grupo sem sair do DropDesk.",
        colorKey: "accent" as const,
    },
    {
        icon: ICONS_DROPDESK.thunder,
        title: "Centralize a comunicação",
        description: "Tenha todas as conversas internas em um só lugar, com segurança.",
        colorKey: "purple" as const,
    },
    {
        icon: ICONS_DROPDESK.growth,
        title: "Mais alinhamento",
        description: "Compartilhe informações e arquivos e acelere a resolução dos atendimentos.",
        colorKey: "success" as const,
    },
    {
        icon: ICONS_DROPDESK.star,
        title: "Produtividade em equipe",
        description: "Ferramentas pensadas para transformar colaboração em resultados.",
        colorKey: "orange" as const,
    },
];

const CHAT_CHECKS = [
    "Conversas privadas e em grupo",
    "Compartilhe informações e arquivos",
    "Mais alinhamento e produtividade",
];

type Props = {
    colors: ColorScheme;
    isAdmin: boolean;
    onClose: () => void;
    onEnjoy: () => void;
};

const ModalInternalChatContent = ({ colors, isAdmin, onClose, onEnjoy }: Props) => {
    const getBenefitColors = (colorKey: typeof BENEFITS[number]["colorKey"]) => {
        if (colorKey === "accent") {
            return { color: colors.accent, background: `${colors.accent}18` };
        }
        if (colorKey === "purple") {
            return { color: PurpleAppColors.accent, background: `${PurpleAppColors.accent}18` };
        }
        if (colorKey === "success") {
            return { color: colors.success, background: `${colors.success}18` };
        }
        return { color: OrangeAppColors.accent, background: `${OrangeAppColors.accent}18` };
    };

    return (
        <Container background={colors.onBackground}>
            <CloseButton
                color={colors.hint}
                hoverBackground={colors.buttonOnBackground}
                onClick={onClose}
            >
                <DropDeskIcon
                    icon={ICONS_DROPDESK.close}
                    size={16}
                    color={colors.hint}
                />
            </CloseButton>

            <Body>
                <LeftColumn
                    background={`${colors.delicateBackground}34`}
                    border={colors.border}
                >
                    <BadgeRow>
                        <Badge background={colors.accent} color={SharedColors.white}>
                            <DropDeskIcon
                                icon={ICONS_DROPDESK.star}
                                size={12}
                                color={SharedColors.white}
                            />
                            Novidade
                        </Badge>
                        <MegaphoneWrapper color={colors.accent}>
                            <DropDeskIcon
                                icon={ICONS_DROPDESK.megaphone}
                                size={44}
                                color={colors.accent}
                            />
                        </MegaphoneWrapper>
                    </BadgeRow>

                    <LeftTitle color={colors.text}>
                        Mais conexão, mais alinhamento,{" "}
                        <TitleAccent color={colors.accent}>mais resultados!</TitleAccent>
                    </LeftTitle>

                    <LeftDescription color={colors.hintInverse}>
                        Para atender ainda melhor o seu negócio, o Chat Interno torna a
                        comunicação da sua equipe mais ágil, conectada e eficiente — tudo
                        dentro do DropDesk.
                    </LeftDescription>

                    <BenefitList>
                        {BENEFITS.map((benefit) => {
                            const benefitColors = getBenefitColors(benefit.colorKey);
                            return (
                                <BenefitItem key={benefit.title}>
                                    <BenefitIcon
                                        background={benefitColors.background}
                                        color={benefitColors.color}
                                    >
                                        <DropDeskIcon
                                            icon={benefit.icon}
                                            size={16}
                                            color={benefitColors.color}
                                        />
                                    </BenefitIcon>
                                    <BenefitText>
                                        <BenefitTitle color={colors.text}>
                                            {benefit.title}
                                        </BenefitTitle>
                                        <BenefitDescription color={colors.hintInverse}>
                                            {benefit.description}
                                        </BenefitDescription>
                                    </BenefitText>
                                </BenefitItem>
                            );
                        })}
                    </BenefitList>

                    <Callout
                        background={`${colors.accent}12`}
                        border={`${colors.accent}28`}
                    >
                        <DropDeskIcon
                            icon={ICONS_DROPDESK.padlock}
                            size={18}
                            color={colors.accent}
                        />
                        <CalloutText color={colors.text}>
                            Segurança, estabilidade e suporte que você já conhece,{" "}
                            <TitleAccent color={colors.accent}>
                                agora com ainda mais valor para o seu negócio.
                            </TitleAccent>
                        </CalloutText>
                    </Callout>
                </LeftColumn>

                <RightColumn>
                    <RightIntro>
                        <DropDeskIcon
                            icon={ICONS_DROPDESK.star}
                            size={16}
                            color={colors.accent}
                        />
                        <RightIntroText>
                            <RightIntroTitle color={colors.text}>
                                Uma novidade, o mesmo compromisso:
                            </RightIntroTitle>
                            <RightIntroSubtitle color={colors.text}>
                                facilitar a comunicação da sua equipe e fazer seu negócio crescer.
                            </RightIntroSubtitle>
                        </RightIntroText>
                    </RightIntro>

                    <FeatureCardCenter>
                        <FeatureCardFixed
                            border={`${colors.delicateBackground}`}
                            background={`${colors.delicateBackground}12`}
                        >
                            <FeatureImage src={chatInternoBanner} alt="Chat Interno" />
                            <FeatureContent>
                                <FeatureTitle color={colors.text}>Chat Interno</FeatureTitle>
                                <FeatureSubtitle color={colors.success}>
                                    Comunique sua equipe com eficiência
                                </FeatureSubtitle>
                                <Checklist>
                                    {CHAT_CHECKS.map((item) => (
                                        <CheckItem key={item}>
                                            <DropDeskIcon
                                                icon={ICONS_DROPDESK.check}
                                                size={12}
                                                color={colors.success}
                                            />
                                            <CheckText color={colors.text}>{item}</CheckText>
                                        </CheckItem>
                                    ))}
                                </Checklist>
                            </FeatureContent>
                            <PriceBox>
                                <PriceLabel color={colors.hintInverse}>Por apenas</PriceLabel>
                                <PriceValue color={colors.success}>R$ 29,90</PriceValue>
                                <PricePeriod color={colors.hintInverse}>por mês</PricePeriod>
                                <PriceNote
                                    background={`${colors.success}12`}
                                    color={colors.success}
                                >
                                    <DropDeskIcon
                                        icon={ICONS_DROPDESK.check}
                                        size={11}
                                        color={colors.success}
                                    />
                                    Por empresa (todos os usuários)
                                </PriceNote>
                            </PriceBox>
                        </FeatureCardFixed>
                    </FeatureCardCenter>

                    <InvestmentCalloutPinned
                        background={`${colors.accent}10`}
                        border={`${colors.accent}22`}
                    >
                        <DropDeskIcon
                            icon={ICONS_DROPDESK.review}
                            size={18}
                            color={colors.accent}
                        />
                        <InvestmentText color={colors.text}>
                            <InvestmentBold color={colors.accent}>
                                Pequeno investimento, grande impacto.
                            </InvestmentBold>
                            Ferramenta pensada para dar mais colaboração e resultado ao seu
                            atendimento — com o padrão DropDesk que você já confia.
                        </InvestmentText>
                    </InvestmentCalloutPinned>

                    <Footer background={colors.onBackground} border={colors.border}>
                        {isAdmin ? (
                            <>
                                <ButtonSecondary
                                    color={colors.text}
                                    border={colors.border}
                                    hoverBackground={colors.buttonOnBackground}
                                    onClick={onClose}
                                >
                                    Talvez depois
                                </ButtonSecondary>
                                <ButtonPrimary
                                    background={colors.accent}
                                    color={colors.textOnAccent}
                                    onClick={onEnjoy}
                                >
                                    Quero aproveitar!
                                </ButtonPrimary>
                            </>
                        ) : (
                            <ButtonPrimary
                                background={colors.accent}
                                color={colors.textOnAccent}
                                onClick={onClose}
                            >
                                Fechar
                            </ButtonPrimary>
                        )}
                    </Footer>
                </RightColumn>
            </Body>
        </Container>
    );
};

export default ModalInternalChatContent;
